import { Box, Container, Typography } from "@material-ui/core"
import {
  postGiftCardGenerateVerificationCode,
  postGiftCardGetGiftCard,
} from "api"
import { useIsDesktop } from "common/MediaQueries"
import { checkBalance, leaveBalancePage } from "common/analytics"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { schemaCommon } from "components/Reusable/Bit/Form/FormikSchema"
import {
  formatGiftCardNumber,
  formatPhoneNumber,
  isNumber,
} from "components/Reusable/Functions/Functions"

import GenericButton from "components/GenericButton/GenericButton"
import { GenericInput } from "components/GenericInput/GenericInput"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import {
  Dispatch,
  SetStateAction,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import * as yup from "yup"
import { BalanceSteps } from "~/pages/balance"
import animationGiftCard from "../../public/animation/OTP Giftcard.json"
import animationPhone from "../../public/animation/OTP phone.json"
import { useOtpContext } from "../OtpStep/OtpContext"
import BalanceHeader from "./BalanceHeader"
import { useGiftCardContext } from "./GiftCardContext"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledBox = styled(Container)`
  padding-left: 16px;
  padding-right: 16px;
  margin-top: 85px;
  form {
    margin-top: 30px;
    height: 80px;
    label {
      transform: translate(-10px, 13px) scale(1);
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      width: 328px;
      height: 40px;
      margin-top: 20px;
      label {
        font-size: 18px;
      }
    }
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    margin-top: 0px;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  }
  .grecaptcha-badge {
    ${(p) => p.theme.breakpoints.down("sm")} {
      bottom: 80px !important;
    }
  }
  .MuiInputBase-root {
    fieldset {
      height: 100%;
      top: 0px;
    }
  }
`

const StyledButtonWrapper = styled(Box)`
  width: 328px;
  margin-top: 20px;
  border-radius: 8px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    position: fixed;
    bottom: 0;
    right: 0;
    width: calc(100% - 32px);
    height: 70px;
    position: fixed;
    border-radius: 10px;
    box-shadow: 0 -5px 10px 0 hsla(0, 0%, 0%, 0.12);
    background-color: #fff;
    padding: 12px 16px 10px 16px;
    margin-top: 0px;
  }
  box-sizing: content-box;
`

const ImageWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 48px;
  margin-bottom: 30px;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 0px;
    margin-bottom: 0px;
  }

  @media (max-height: 749px) {
    margin-top: 30px;
  }
`

const StyledBoxWrapper = styled(Box)`
  position: relative;
  z-index: 9;
  ${(p) => p.theme.breakpoints.up("md")} {
    order: 1;
    height: 350px;
    margin-right: 60px;
  }

  @media (max-height: 749px) {
    margin-top: 100px;
  }
`

const StyledLottie = styled(Lottie)<{ $isDesktop: boolean; $switcher: number }>`
  height: ${(p) => (p.$isDesktop ? "450px" : "200px")};
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 20px;
    width: ${(p) => (p.$isDesktop ? "250px" : "140px")};
    transform: scale(2);
    margin-right: 65px;
  }
`

const StyledError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  line-height: 1.1;
  font-size: 18px;
  font-family: Speedee, NarkisBlock, sans-serif;
  text-align: right;
  position: absolute;
  margin-top: -25px;
`

const StyledGenerticButton = styled(GenericButton)<{ isdisabled: boolean }>`
  ${(p) =>
    p.isdisabled &&
    `
    background: var(--Light-Grey, #f7f7f7);
    color: rgba(45, 45, 45, 0.72);
    font-size: 24px;
    :hover {
    background: var(--Light-Grey, #f7f7f7);

  }
  `}
`

const StyledInputWrapper = styled.div`
  > * {
    width: 100%;
  }

  .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl {
    width: 100%;
  }
`

const schemaPhone = yup
  .object({
    phone: schemaCommon.phoneNumber,
  })
  .defined()

const schemaCardNumber = yup
  .object({
    cardNumber: schemaCommon.cardNumber,
  })
  .defined()

type BalancePageProps = {
  setBalanceteps: React.Dispatch<React.SetStateAction<number>>
  setRefList: Dispatch<SetStateAction<any[]>>
}

const BalancePage = ({ setBalanceteps, setRefList }: BalancePageProps) => {
  const [switcher, setSwitcher] = useState(0)
  const { setGiftCard } = useGiftCardContext()
  const { setTotalTimeoutInSeconds, setPhoneNumber } = useOtpContext()
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const ref = useRef(null)
  const [isValid, setIsValid] = useState(false)
  const [cardMaxLength, setCardMaxLength] = useState(15)
  const [giftCardError, setGiftCardError] = useState(null)

  const Section1Ref = useRef(null)
  const reCaptchaRef = createRef<ReCAPTCHA>()

  useEffect(() => {
    ref?.current?.setSpeed(0.7)
  }, [ref])

  useEffect(() => {
    checkBalance()
    setRefList([Section1Ref])
  }, [])

  const validateSubmitButton = (inputType, res, value) => {
    // Phone
    if (inputType === 0)
      return setIsValid(
        Object.keys(res).length === 0 &&
          formatPhoneNumber(value).replace(/[^0-9]/g, "").length === 10
          ? true
          : false
      )
    // Card
    if (inputType === 1)
      return setIsValid(
        Object.keys(res).length === 0 &&
          value?.trim().replace(/[^0-9]/g, "").length === 15
          ? true
          : false
      )
  }

  const buttonValid = async (formik, value) => {
    if (switcher === 0) {
      const formattedPhone = formatPhoneNumber(value)
      formik.setFieldValue("phone", formattedPhone)
    } else {
      formik.setFieldValue("cardNumber", formatGiftCard(value))
    }
    switcher === 0
      ? await formik.validateField("phone")
      : await formik.validateField("cardNumber")
    const res = await formik.validateForm()
    validateSubmitButton(switcher, res, value)
  }

  const formatGiftCard = (value: string) => {
    setGiftCardError(null)
    setCardMaxLength(18)
    return formatGiftCardNumber(value)
  }

  const isNumberTyped = (evt) => {
    //only allow numbers without '-' symbol
    if (!isNumber(evt) || (switcher === 0 && evt.target.value.length > 10)) {
      evt.preventDefault()
    }
  }

  const getGiftCard = async (
    cardNumber: string,
    gRecaptchaResponse: string
  ) => {
    const response = await postGiftCardGetGiftCard({
      code: cardNumber.replace(/-/g, ""),
      gRecaptchaResponse,
    })

    if (response?.status === 200 && response?.data) {
      if (response.data.body) {
        setGiftCardError(null)
        setGiftCard(response.data.body)
        leaveBalancePage("giftCardNumber", response.data?.body?.balance)
        if (response.data?.body?.guid) {
          router.push(
            `/balance/giftcard?giftCardGuid=${response.data?.body?.guid}`
          )
        } else {
          router.push("/balance/giftcard")
        }
      } else {
        setGiftCardError("*בבקשה לבדוק שהזנת מס' גיפטקארד תקין")
        setTimeout(() => {
          const el = document?.getElementById("cardNumber") as HTMLInputElement
          el?.focus()
          el?.select()
        }, 0)
      }
    } else {
      router.push("/balance")
    }
  }

  const getVerificationCode = async (
    phone: string,
    gRecaptchaResponse: string
  ) => {
    setBalanceteps(BalanceSteps.LOADER)
    try {
      const response = await postGiftCardGenerateVerificationCode({
        mobilePhone: phone.replace(/-/g, ""),
        toSendSms: true,
        gRecaptchaResponse,
      })
      if (response?.status === 200 && response?.data) {
        router.push("/balance/otp")
        leaveBalancePage("phoneNumber")
        setTotalTimeoutInSeconds(response.data.body.totalTimeoutInSeconds)
      }
    } catch (error) {
      setBalanceteps(BalanceSteps.FAIL)
    }
  }

  return (
    <StyledBox>
      <StyledBoxWrapper ref={Section1Ref}>
        <BalanceHeader
          setSwitcher={setSwitcher}
          setGiftCardError={setGiftCardError}
        />
        <FormikForm
          initialValues={{ phone: "", cardNumber: "" }}
          validationSchema={switcher === 0 ? schemaPhone : schemaCardNumber}
          onSubmit={async (values) => {
            reCaptchaRef?.current?.reset()
            const recaptchaResponse =
              await reCaptchaRef?.current?.executeAsync()
            if (switcher === 1) {
              await getGiftCard(values.cardNumber, recaptchaResponse)
            } else {
              setPhoneNumber(values.phone.replace(/-/g, ""))
              await getVerificationCode(values.phone, recaptchaResponse)
            }
          }}
          render={(formik) => (
            <>
              <StyledInputWrapper>
                <GenericInput
                  name={switcher === 0 ? "phone" : "cardNumber"}
                  label={
                    switcher === 0 ? "מס’ הטלפון שלי" : "מס’ הגיפטקארד שלי"
                  }
                  type="text"
                  $mobileWidth="100%"
                  id={switcher === 0 ? "phone" : "cardNumber"}
                  maxLength={switcher !== 0 ? cardMaxLength : 99}
                  numeric={true}
                  autoComplete={switcher === 0 ? "tel" : "off"}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (switcher === 0) {
                      formik.setFieldTouched("phone")
                      formik.setFieldValue("phone", e.target.value)
                      await buttonValid(formik, e.target.value)
                    } else {
                      formik.setFieldTouched("cardNumber")
                      formik.setFieldValue("cardNumber", e.target.value)
                      await buttonValid(formik, e.target.value)
                    }
                  }}
                  onKeyUp={(e) => {
                    {
                      isNumberTyped(e)
                    }
                  }}
                />
              </StyledInputWrapper>

              {giftCardError && (
                <StyledError
                  aria-describedby={switcher === 0 ? "phone" : "cardNumber"}
                >
                  {giftCardError}
                </StyledError>
              )}
              <StyledButtonWrapper>
                <StyledGenerticButton
                  btnType="filled"
                  isdisabled={!isValid}
                  type="submit"
                >
                  כמה נשאר לי
                </StyledGenerticButton>
              </StyledButtonWrapper>
              <ReCAPTCHA
                ref={reCaptchaRef}
                sitekey={RECAPTCHA_KEY}
                size="invisible"
                hl="iw"
                tabIndex={-1}
              />
            </>
          )}
        />
      </StyledBoxWrapper>
      <ImageWrapper>
        <StyledLottie
          loop={false}
          lottieRef={ref}
          animationData={switcher === 0 ? animationPhone : animationGiftCard}
          $isDesktop={isDesktop}
          $switcher={switcher}
          role="presentation"
        />
        <img
          className="MuiTypography-srOnly"
          src="/"
          alt={"בירור יתרה מקדונלד'ס גיפטקארד"}
          loading="lazy"
        />
      </ImageWrapper>
    </StyledBox>
  )
}

export default BalancePage
